import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  type FormListSelectionChangedArgs,
  FormListSelectionOption,
} from 'donor-elf-web/components/form/list-selection';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { getChangesetErrorsForProperty } from 'donor-elf-web/lib/changeset-utils';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import ContactsCache, {
  type ContactCustomPropertyInfo,
} from 'donor-elf-web/services/contacts-cache';

// if this is a list type property, then it will add 'o' to the
// args.changeset which will be a string array and include all the options,
// including new ones the user added but didn't select

interface ComponentArgs {
  propertyInfo: ContactCustomPropertyInfo;
  changeset: any;
  showValidationErrors: boolean;
  dropdownInitiallyOpened?: boolean; // open the dropdown immediately (if the property value is a dropdown)
}

const TextTypes: any = {
  s: true,
  n: true,
  c: true,
};

export default class ContactDetailsPropertiesCustomPropertyEditValueComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    if (this.listType) {
      let currentValue = args.changeset.get('v') || {};
      let options =
        args.propertyInfo.o ||
        this.contactsCache.customPropertiesByLowecaseLabel[
          args.propertyInfo.l.toLowerCase()
        ].o ||
        [];
      this.listSelectionOptions = sortByProperties(
        options.map((x, index) => {
          return new FormListSelectionOption({
            val: x,
            selected: currentValue[x] || false,
            index,
          });
        }),
        ['selected:desc', 'index:asc'],
      );
    }
  }
  @service declare contactsCache: ContactsCache;
  @tracked listSelectionOptions?: FormListSelectionOption[];

  get textValueType() {
    return (
      this.args.propertyInfo.t === undefined ||
      TextTypes[this.args.propertyInfo.t] === true
    );
  }

  get booleanType() {
    return this.args.propertyInfo.t === 'b';
  }

  get listType() {
    return this.args.propertyInfo.t === 'l';
  }

  get booleanOptions(): SelectOption[] {
    return [
      {
        val: true,
        label: 'Yes',
      },
      {
        val: false,
        label: 'No',
      },
    ];
  }

  get singleSelectionList() {
    return (
      this.args.propertyInfo?.s?.single_selection ||
      this.contactsCache.customPropertiesByLowecaseLabel[
        this.args.propertyInfo.l.toLowerCase()
      ]?.s?.single_selection
    );
  }

  @action
  handleListSelectionsChanged(args: FormListSelectionChangedArgs) {
    let newValue: { [val: string]: true } = {};
    for (let option of args.selectedListOptions) {
      newValue[option.val] = true;
    }
    this.args.changeset.set('v', newValue);
    this.args.changeset.set(
      'o',
      args.allListOptions.map((x) => x.val),
    );
  }

  get errorMessage() {
    if (this.args.showValidationErrors) {
      return getChangesetErrorsForProperty(this.args.changeset, 'v')?.join(
        ', ',
      );
    }
  }
}
